import {useCallback, useEffect, useRef, useState} from "react";
import type {PricingCountry} from "pricing/planCatalogue";
import {LadderOfferApi} from "./LadderOfferApi";
import {currentLadderOfferLoading} from "./currentLadderOffer";
import {couponAdmissionKey, retainsCouponAnswer, couponPresentation, type CouponRefusal, type ReceivedCouponAnswer} from "./couponCountdown";

/**
 * Holds request state and a clock; account and offer eligibility stay in the React-free domain.
 *
 * @param campaignLoading a higher-priority campaign request is in flight for the same slot. Whether
 *   that empties the slot or leaves a coupon standing is `couponAdmissionKey`'s decision, not this
 *   adapter's — it only supplies the answer it is holding.
 */
export function useCurrentLadderOffer(accountKey: string | null, country: PricingCountry | null,
	transferId: string | null = null, campaignLoading = false) {
	const [answer, setAnswer] = useState<ReceivedCouponAnswer | null>(null);
	const [now, setNow] = useState(Date.now);
	const [refusal, setRefusal] = useState<CouponRefusal | null>(null);
	const latestAnswer = useRef(answer);
	latestAnswer.current = answer;
	const admittedKey = couponAdmissionKey(accountKey, transferId, answer, campaignLoading);
	useEffect(() => {
		if (retainsCouponAnswer(admittedKey, transferId, latestAnswer.current)) return;
		setAnswer(null);
		if (admittedKey === null) return;
		let active = true;
		void LadderOfferApi.currentOffer(country, transferId, admittedKey).then(resolution => {
			if (!active) return;
			const receivedAt = Date.now();
			setAnswer({accountKey: admittedKey, transferId, resolution, receivedAt});
			setNow(receivedAt);
		});
		return () => { active = false; };
	}, [admittedKey, country, transferId]);
	const presentation = couponPresentation(admittedKey, answer, now, refusal);
	useEffect(() => {
		if (presentation.offer === null) return;
		const tick = () => setNow(Date.now());
		const timer = setInterval(tick, 1_000);
		window.addEventListener("focus", tick);
		document.addEventListener("visibilitychange", tick);
		return () => {
			clearInterval(timer);
			window.removeEventListener("focus", tick);
			document.removeEventListener("visibilitychange", tick);
		};
	}, [presentation.offer]);
	const expire = useCallback(() => {
		if (presentation.offer === null) return;
		const at = Date.now();
		setRefusal({presentationId: presentation.offer.presentationId, at});
		setNow(at);
	}, [presentation.offer]);
	return {...presentation, expire, loading: currentLadderOfferLoading(admittedKey, answer), now};
}
